import React, { Fragment, useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getNotificationById, createNotification, updateNotification } from '../../actions/notification';
import { 
  ArrowLeft, 
  Save, 
  Bell,
  Calendar,
  User,
  AlertCircle
} from 'lucide-react';

const initialState = {
  user: '',
  notificationType: '',
  title: '',
  description: '',
  expireDate: '',
  notified: false,
  read: false,
  expired: false
};

const Notification = ({
  getNotificationById,
  createNotification,
  updateNotification,
  notification: { notification, loading }
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const isNewItem = id === "new";

  useEffect(() => {
    // Set document title based on whether we're editing or creating
    document.title = isNewItem ? 'Create Notification' : 'Edit Notification';
    
    if (!isNewItem && (!notification || notification._id !== id)) {
      getNotificationById(id);
    }
  }, [getNotificationById, id, isNewItem, notification]);

  useEffect(() => {
    if (!loading && notification && !isNewItem) {
      const notificationData = { ...initialState };
      for (const key in notification) {
        if (key in notificationData) {
          notificationData[key] = notification[key];
        }
      }
      
      // Format date for input field
      if (notificationData.expireDate) {
        const date = new Date(notificationData.expireDate);
        notificationData.expireDate = date.toISOString().split('T')[0];
      }
      
      setFormData(notificationData);
    }
  }, [loading, notification, isNewItem]);

  const {
    user,
    notificationType,
    title,
    description,
    expireDate,
    notified,
    read,
    expired
  } = formData;

  const onChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const onSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (isNewItem) {
        const newItemId = await createNotification(formData, navigate);
        if (newItemId) {
          navigate(`/inventory/v1.2/notifications/${newItemId}`);
        }
      } else {
        const success = await updateNotification(id, formData);
        // Stay on the same page after update
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return loading && !isNewItem ? (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  ) : (
    <Fragment>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Link to="/inventory/v1.2/notifications" className="btn btn-outline-primary d-flex align-items-center mb-2">
              <ArrowLeft size={16} className="me-2" />
              Back to Notifications
            </Link>
            <h1 className="h3 mb-0 text-gray-800">
              {isNewItem ? 'Create New Notification' : `Edit ${title}`}
            </h1>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0 d-flex align-items-center">
              <Bell size={18} className="me-2 text-primary" />
              {isNewItem ? 'Create Details' : 'Edit Details'}
            </h5>
          </div>
          <div className="card-body">
            <form onSubmit={onSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">User ID</label>
                  <input
                    type="text"
                    className="form-control"
                    name="user"
                    value={user}
                    onChange={onChange}
                    placeholder="Enter user ID"
                  />
                  <small className="text-muted">
                    MongoDB ObjectId of the user to notify (optional)
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Notification Type</label>
                  <select
                    className="form-select"
                    name="notificationType"
                    value={notificationType}
                    onChange={onChange}
                    required
                  >
                    <option value="">Select notification type</option>
                    <option value="stock">Stock</option>
                    <option value="vm-status">VM Status</option>
                    <option value="cans">Cans</option>
                    <option value="payment">Payment</option>
                    <option value="order">Order</option>
                    <option value="update">Update</option>
                    <option value="offers">Offers</option>
                    <option value="lock-status">Lock Status</option>
                    <option value="delivered">Delivered</option>
                    <option value="delivery">Delivery</option>
                  </select>
                  <small className="text-muted">
                    Type of notification to send
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={title}
                    onChange={onChange}
                    placeholder="Enter notification title"
                    required
                  />
                  <small className="text-muted">
                    Brief title for the notification
                  </small>
                </div>
                
                <div className="col-md-6">
                  <label className="form-label fw-bold">Expire Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="expireDate"
                    value={expireDate}
                    onChange={onChange}
                  />
                  <small className="text-muted">
                    When this notification should expire (optional)
                  </small>
                </div>
                
                <div className="col-12">
                  <label className="form-label fw-bold">Description</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    name="description"
                    value={description}
                    onChange={onChange}
                    placeholder="Enter detailed notification description"
                    required
                  />
                  <small className="text-muted">
                    Detailed description of the notification
                  </small>
                </div>
                
                <div className="col-md-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="notified"
                      checked={notified}
                      onChange={onChange}
                      id="notifiedSwitch"
                    />
                    <label className="form-check-label" htmlFor="notifiedSwitch">
                      Notified
                    </label>
                  </div>
                  <small className="text-muted d-block">
                    Whether the user has been notified
                  </small>
                </div>
                
                <div className="col-md-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="read"
                      checked={read}
                      onChange={onChange}
                      id="readSwitch"
                    />
                    <label className="form-check-label" htmlFor="readSwitch">
                      Read
                    </label>
                  </div>
                  <small className="text-muted d-block">
                    Whether the notification has been read
                  </small>
                </div>
                
                <div className="col-md-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="expired"
                      checked={expired}
                      onChange={onChange}
                      id="expiredSwitch"
                    />
                    <label className="form-check-label" htmlFor="expiredSwitch">
                      Expired
                    </label>
                  </div>
                  <small className="text-muted d-block">
                    Whether the notification has expired
                  </small>
                </div>
              </div>
              
              <div className="d-flex justify-content-end mt-4">
                <Link to="/inventory/v1.2/notifications" className="btn btn-outline-secondary me-2">
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-2" />
                      {isNewItem ? 'Create' : 'Update'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

Notification.propTypes = {
  getNotificationById: PropTypes.func.isRequired,
  createNotification: PropTypes.func.isRequired,
  updateNotification: PropTypes.func.isRequired,
  notification: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  notification: state.notification
});

export default connect(
  mapStateToProps,
  { getNotificationById, createNotification, updateNotification }
)(Notification);
